import { AfterViewChecked, Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Event } from 'src/app/model/event';
import { Submission } from 'src/app/model/paper';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { NavbarService } from 'src/app/service/navbar.service';
import { SidebarService } from 'src/app/service/sidebar.service';
import { SidebarComponent } from 'src/app/component/sidebar/sidebar.component';
import { SelectionClipboardService } from 'src/app/service/selection-clipboard.service';
import { LocalCache } from 'src/app/enum/local.cache';

// Página "Sessões que coordeno" — lista as submissões em que o usuário
// atua como Session Chair (coordenador de sessão técnica), agrupadas
// por evento. Consome o endpoint `/core/submission/byUserSessionChair/`
// SEM o filtro `event` (backend agora aceita event opcional → retorna
// todas as submissões do usuário como session chair).
class SubmissionsByEvent {
  event: Event;
  count: number;
  submissions: Array<Submission>;
}

@Component({
  selector: 'app-session-chairings',
  templateUrl: './session-chairings.component.html',
  styleUrls: ['./session-chairings.component.scss']
})
export class SessionChairingsComponent implements OnInit, AfterViewChecked {
  public submissionsByEvent: SubmissionsByEvent[] = null;
  public search: string = '';
  public isScrolled = false;
  // Toggle "Incluir eventos antigos" — persistido em LocalCache.
  public showAll: boolean = !!LocalCache.getValue(LocalCache.OLD_SESSION_CHAIRINGS);

  // Seleção pra copy-to-clipboard — mesmo padrão de Minhas submissões.
  public selectedIds = new Set<number>();

  constructor(
    private submissionService: SubmissionsService,
    public navbarService: NavbarService,
    private sidebarService: SidebarService,
    private selectionClipboard: SelectionClipboardService,
    private host: ElementRef<HTMLElement>,
  ) {}

  // Notificação vinda do card quando o session chair altera o rádio de
  // Apresentação (no-show). Reexecuta a contagem do badge da sidebar.
  public onSubmissionUpdated(): void {
    const sidebar = this.sidebarService?.sidebar as SidebarComponent;
    sidebar?.refreshSessionChairingsBadges?.();
  }

  // Iguala a largura das colunas dentro de CADA .event-group: mede a
  // maior `.snc-id-col`, `.snc-session-col` e `.snc-files-aside`, e
  // aplica como min-width nas demais. Roda em AfterViewChecked (barato
  // — só reaplica quando o valor muda) + resize da janela.
  @HostListener('window:resize')
  onResize(): void { this.equalizeColumns(); }
  ngAfterViewChecked(): void { this.equalizeColumns(); }

  private equalizeColumns(): void {
    const groups = this.host.nativeElement.querySelectorAll<HTMLElement>('.event-group');
    groups.forEach(group => {
      ['.snc-id-col', '.snc-session-col', '.snc-files-aside'].forEach(sel => {
        const cols = Array.from(group.querySelectorAll<HTMLElement>(sel));
        if (cols.length < 2) { return; }
        const widths = cols.map(c => c.offsetWidth);
        const max = Math.max(...widths);
        // Já iguais? não mexer — evita loop com AfterViewChecked.
        if (widths.every(w => w === max)) { return; }
        const px = `${max}px`;
        cols.forEach(c => { c.style.minWidth = px; });
      });
    });
  }

  public toggleSelection(id: number, checked: boolean): void {
    if (checked) { this.selectedIds.add(id); } else { this.selectedIds.delete(id); }
  }

  public clearSelection(): void {
    this.selectedIds.clear();
  }

  public async copySelection(): Promise<void> {
    const flat: Submission[] = (this.submissionsByEvent || [])
      .flatMap(g => g?.submissions || []);
    const items = flat.filter(s => this.selectedIds.has(s.id));
    await this.selectionClipboard.copySubmissions(items);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 4;
  }

  ngOnInit(): void {
    this.load();
  }

  public trackByEventId(_i: number, g: SubmissionsByEvent): number {
    return g?.event?.id;
  }

  public trackBySubmissionId(_i: number, s: Submission): number {
    return s?.id;
  }

  public formatEventUrl(event: any): string {
    if (!event?.uri) { return ''; }
    return `${window.location.origin}/${event.uri}`;
  }

  public formatEventLocation(event: any): string {
    const info = event?.eventInfo;
    if (!info) { return ''; }
    const cityState = [info.city, info.state].filter(v => !!v).join(' - ');
    return [cityState, info.country].filter(v => !!v).join(', ');
  }

  public onToggleShowAll(): void {
    LocalCache.setValue(LocalCache.OLD_SESSION_CHAIRINGS, this.showAll);
    this.selectedIds.clear();
    this.load();
  }

  public load(): void {
    this.navbarService.loadingStart();
    this.submissionsByEvent = null;
    // `event=null` faz o service pular o param → backend retorna todos
    // os eventos onde o usuário é session chair. `show=all` inclui os
    // eventos antigos (encerrados).
    const show = this.showAll ? 'all' : 'active';
    this.submissionService.getSessionsSubmissionsBySessionChair(null, null, show).subscribe(page => {
      const subs = (page?.items || []) as Submission[];
      this.submissionsByEvent = this.groupByEvent(subs);
      this.navbarService.loadingStop();
    }, () => {
      this.submissionsByEvent = [];
      this.navbarService.loadingStop();
    });
  }

  private groupByEvent(subs: Submission[]): SubmissionsByEvent[] {
    const byId = new Map<number, SubmissionsByEvent>();
    for (const s of subs) {
      const ev = s.event;
      if (!ev) { continue; }
      let g = byId.get(ev.id);
      if (!g) {
        g = new SubmissionsByEvent();
        g.event = ev;
        g.submissions = [];
        byId.set(ev.id, g);
      }
      g.submissions.push(s);
    }
    byId.forEach(g => { g.count = g.submissions.length; });
    return Array.from(byId.values()).sort((a, b) => a.event.id - b.event.id);
  }
}
